import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Box, CssBaseline, AppBar, Toolbar, Typography, List, ListItem, ListItemIcon, ListItemText, Badge, Tooltip, IconButton, CircularProgress, Drawer, useMediaQuery, InputBase, Paper, ClickAwayListener } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { bloomberg } from './theme/bloombergTheme';
import { AppThemeProvider, useThemeMode } from './theme/ThemeContext';
import { migrateIfNeeded, getPortfolios, getTickers, getWatchlist as getLocalWatchlist } from './utils/portfolioStorage';

migrateIfNeeded();
import DashboardIcon from '@mui/icons-material/Dashboard';
import TableChartIcon from '@mui/icons-material/TableChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FeedIcon from '@mui/icons-material/Feed';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import MapIcon from '@mui/icons-material/Map';
import PublicIcon from '@mui/icons-material/Public';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InsightsIcon from '@mui/icons-material/Insights';
import SyncIcon from '@mui/icons-material/Sync';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
// import LogoutIcon from '@mui/icons-material/Logout';
import { styled } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import AdminPortal from './pages/AdminPortal';
import api, { syncWatchlistUp } from './services/api';

// Page imports
import Dashboard from './pages/Dashboard';
import Portfolio from './pages/Portfolio';
import StockScreener from './pages/StockScreener';
import Patterns from './pages/Patterns';
import ResearchChat from './pages/ResearchChat';
import Research from './pages/Research';
import MarketMap from './pages/MarketMap';
import MacroOverview from './pages/MacroOverview';
import CorrelationStudies from './pages/CorrelationStudies';
import Charting from './pages/Charting';
import News from './pages/News';
import Podcasts from './pages/Podcasts';
import Notifications from './pages/Notifications';
import Signals from './pages/Signals';
import Settings from './pages/Settings';
import StockDetail from './pages/StockDetail';
import Docs from './pages/Docs';
import TrackerPortfolio from './pages/TrackerPortfolio';
import JobNotifier from './components/JobNotifier';

const drawerWidth = 220;
const drawerCollapsedWidth = 56;

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  paddingLeft: `calc(${drawerCollapsedWidth}px + ${theme.spacing(2)})`,
  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(2),
  },
  background: bloomberg.bg.primary,
  minHeight: '100vh',
  width: '100%',
}));

// Navigation items configuration. Tracker, Correlation and Research are hidden
// from the sidebar (Oct 2026, before sharing outside Marc and Damien); their
// routes still work.
const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { path: '/portfolio', label: 'Portfolio', icon: <TableChartIcon /> },
  { path: '/market-map', label: 'Market Map', icon: <MapIcon /> },
  { path: '/macro-overview', label: 'Macro', icon: <PublicIcon /> },
  { path: '/charting', label: 'Charting', icon: <CompareArrowsIcon /> },
  { path: '/screener', label: 'Screener', icon: <SearchIcon /> },
  { path: '/patterns', label: 'Patterns', icon: <CandlestickChartIcon /> },
  // Ask Research hidden for now — route still exists below, just not in nav.
  // { path: '/ask-research', label: 'Ask Research', icon: <AutoAwesomeIcon /> },
  { path: '/podcasts', label: 'Podcasts', icon: <PodcastsIcon /> },
  { path: '/signals', label: 'Signals', icon: <InsightsIcon /> },
  { path: '/news', label: 'News', icon: <FeedIcon /> },
  { path: '/notifications', label: 'Alerts', icon: <NotificationsIcon /> },
  { path: '/settings', label: 'Settings', icon: <SettingsIcon />, dividerBefore: true },
];

interface SidebarProps {
  isMobile: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

// Shared nav list used by both desktop sidebar and mobile drawer
const NavList = ({ expanded, onNavigate }: { expanded: boolean; onNavigate?: () => void }) => {
  const [alertBadge, setAlertBadge] = React.useState(0);
  const currentPath = window.location.pathname;

  React.useEffect(() => {
    const fetchCount = () => {
      api.get(`/api/alerts/triggered/count`)
        .then((res) => setAlertBadge(res.data.count || 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <List sx={{ py: 0.5 }}>
      {navItems.map((item) => {
        const isActive = currentPath === item.path ||
          (item.path !== '/' && currentPath.startsWith(item.path));

        return (
          <React.Fragment key={item.path}>
            {item.dividerBefore && (
              <Box sx={{ my: 0.5, mx: 1.5, borderBottom: `1px solid ${bloomberg.border.primary}` }} />
            )}
            <ListItem disablePadding sx={{ mb: 0 }}>
              <ListItemButton
                component={NavLink}
                to={item.path}
                onClick={onNavigate}
                sx={{
                  color: isActive ? bloomberg.accent.orange : bloomberg.text.secondary,
                  backgroundColor: isActive ? bloomberg.accent.orangeDim : 'transparent',
                  borderLeft: isActive ? `2px solid ${bloomberg.accent.orange}` : '2px solid transparent',
                  justifyContent: expanded ? 'initial' : 'center',
                  px: 2,
                  py: 0.8,
                  minHeight: 36,
                  '&:hover': {
                    backgroundColor: isActive ? bloomberg.accent.orangeDim : bloomberg.bg.hover,
                    color: isActive ? bloomberg.accent.orange : bloomberg.text.primary,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? bloomberg.accent.orange : bloomberg.text.muted,
                    minWidth: 0,
                    mr: expanded ? 2 : 'auto',
                    justifyContent: 'center',
                    '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                  }}
                >
                  {item.path === '/notifications' && alertBadge > 0 ? (
                    <Badge badgeContent={alertBadge} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16 } }}>
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    opacity: expanded ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    '& .MuiListItemText-primary': {
                      fontWeight: isActive ? 600 : 400,
                      whiteSpace: 'nowrap',
                      fontSize: '0.8rem',
                      letterSpacing: '0.02em',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </React.Fragment>
        );
      })}
    </List>
  );
};

// Sidebar component with navigation
const Sidebar = ({ isMobile, mobileOpen, onMobileClose }: SidebarProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            background: bloomberg.bg.secondary,
            borderRight: `1px solid ${bloomberg.border.primary}`,
            color: bloomberg.text.primary,
            top: 40,
            height: 'calc(100vh - 40px)',
          },
        }}
      >
        <NavList expanded={true} onNavigate={onMobileClose} />
      </Drawer>
    );
  }

  return (
    <Box
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      sx={{
        position: 'fixed',
        left: 0,
        top: 40,
        height: 'calc(100vh - 40px)',
        width: isExpanded ? drawerWidth : drawerCollapsedWidth,
        background: bloomberg.bg.secondary,
        borderRight: `1px solid ${bloomberg.border.primary}`,
        color: bloomberg.text.primary,
        transition: 'width 0.2s ease',
        overflowX: 'hidden',
        overflowY: 'auto',
        zIndex: 1200,
      }}
    >
      <Box sx={{ overflow: 'hidden' }}>
        <NavList expanded={isExpanded} />
      </Box>
    </Box>
  );
};

// ── Global stock search bar ───────────────────────────────────────────────────
const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ symbol: string; name: string; sector?: string; country?: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/api/correlation/search?q=${encodeURIComponent(query)}&limit=8`);
        setResults(data.results || []);
        setOpen(true);
        setActiveIdx(-1);
      } catch { setResults([]); }
    }, 200);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [query]);

  const select = (symbol: string) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    setFocused(false);
    inputRef.current?.blur();
    navigate(`/stock/${symbol}`);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); select(results[activeIdx].symbol); }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setFocused(false); }}>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          bgcolor: focused ? bloomberg.bg.tertiary : bloomberg.bg.secondary,
          border: `1px solid ${focused ? bloomberg.accent.orange : bloomberg.border.primary}`,
          borderRadius: '4px', px: 1, py: 0.3,
          transition: 'all 0.15s ease',
          width: focused ? 260 : 180,
        }}>
          <SearchIcon sx={{ fontSize: 14, color: focused ? bloomberg.accent.orange : bloomberg.text.muted, flexShrink: 0 }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); if (!e.target.value) setOpen(false); }}
            onFocus={() => setFocused(true)}
            onKeyDown={handleKey}
            placeholder="Search ticker or name…"
            sx={{
              fontSize: '0.75rem', color: bloomberg.text.primary,
              '& input': { p: 0, '&::placeholder': { color: bloomberg.text.muted, opacity: 1 } },
            }}
          />
          {query && (
            <Box component="span" onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
              sx={{ fontSize: 13, color: bloomberg.text.muted, cursor: 'pointer', lineHeight: 1, '&:hover': { color: bloomberg.text.primary } }}>✕</Box>
          )}
        </Box>

        {open && results.length > 0 && (
          <Paper elevation={8} sx={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`,
            borderRadius: '4px', overflow: 'hidden', zIndex: 9999, minWidth: 260,
          }}>
            {results.map((r, i) => (
              <Box key={r.symbol} onClick={() => select(r.symbol)} onMouseEnter={() => setActiveIdx(i)}
                sx={{
                  px: 1.5, py: 0.9, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5,
                  bgcolor: i === activeIdx ? bloomberg.bg.hover : 'transparent',
                  borderBottom: i < results.length - 1 ? `1px solid ${bloomberg.border.primary}` : 'none',
                  '&:hover': { bgcolor: bloomberg.bg.hover },
                }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: bloomberg.accent.orange, fontFamily: '"IBM Plex Mono", monospace', minWidth: 52 }}>
                  {r.symbol}
                </Typography>
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: bloomberg.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.name}
                  </Typography>
                  {(r.sector || r.country) && (
                    <Typography sx={{ fontSize: '0.65rem', color: bloomberg.text.muted }}>
                      {[r.sector, r.country].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        {open && query && results.length === 0 && (
          <Paper elevation={8} sx={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`,
            borderRadius: '4px', px: 1.5, py: 1, zIndex: 9999,
          }}>
            <Typography sx={{ fontSize: '0.75rem', color: bloomberg.text.muted }}>No results found</Typography>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

function App() {
  return (
    <AppThemeProvider>
      <AuthProvider>
        <Router>
          <AuthGate />
        </Router>
      </AuthProvider>
    </AppThemeProvider>
  );
}

// Gate the whole app: unauthenticated visitors see the landing page; everyone
// else gets the full terminal. Token validity is checked in AuthContext.
function AuthGate() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#03060d' }}>
        <CircularProgress sx={{ color: '#ff8c00' }} />
      </Box>
    );
  }
  if (!isAuthenticated) return <LandingPage />;
  return <AppContent />;
}

// Admin-only route: non-admins are redirected to the dashboard.
function AdminRoute() {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <AdminPortal />;
}

function AppContent() {
  useLocation(); // triggers re-render on route change so NavList isActive updates
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dataStatus, setDataStatus] = useState<{ latest_daily_close: string | null; historical_sync_running: boolean } | null>(null);
  const { username, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  // Spine sync: push client-side portfolio + legacy watchlist symbols to the
  // server-side watchlist so alerts/brief can monitor them. Once per session.
  useEffect(() => {
    try {
      const tickers = new Set<string>();
      const watch = new Set<string>();
      getPortfolios().forEach(p => {
        getTickers(p.id).forEach(t => tickers.add(t));
        getLocalWatchlist(p.id).forEach(w => watch.add(w));
      });
      if (tickers.size || watch.size) {
        syncWatchlistUp(Array.from(tickers), Array.from(watch)).catch(() => {});
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  const fetchDataStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/data/status`);
      setDataStatus(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchDataStatus();
    const interval = setInterval(fetchDataStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchDataStatus]);

  const handleSync = async () => {
    if (dataStatus?.historical_sync_running) return;
    try {
      await api.post(`/api/data/sync`);
      setDataStatus(prev => prev ? { ...prev, historical_sync_running: true } : null);
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/api/data/status`);
          setDataStatus(data);
          if (!data.historical_sync_running) clearInterval(poll);
        } catch { clearInterval(poll); }
      }, 5000);
    } catch { /* silent */ }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1100,
          background: `${bloomberg.bg.primary} !important`,
          borderBottom: `1px solid ${bloomberg.border.primary}`,
          boxShadow: 'none',
          minHeight: '40px !important',
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: '40px !important',
            px: 2,
          }}
        >
          {isMobile && (
            <IconButton
              size="small"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 1, color: bloomberg.text.primary, '&:hover': { color: bloomberg.accent.orange } }}
            >
              <MenuIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: bloomberg.accent.orange,
              boxShadow: `0 0 6px ${bloomberg.accent.orange}`,
            }} />
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontSize: '0.85rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: bloomberg.accent.orange,
                fontFamily: bloomberg.font.heading,
              }}
            >
              Factory Analytics
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, mr: 1.5 }}>
            <GlobalSearch />
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}>
            {/* Latest data date indicator */}
            {dataStatus?.latest_daily_close && (
              <Typography variant="caption" sx={{ color: bloomberg.text.muted, fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.65rem' }}>
                DATA{' '}
                <Box component="span" sx={{
                  color: (() => {
                    const d = new Date(dataStatus.latest_daily_close + 'T00:00:00');
                    const today = new Date(); today.setHours(0,0,0,0);
                    const diff = Math.round((today.getTime() - d.getTime()) / 86400000);
                    return diff <= 1 ? bloomberg.status.positive : diff <= 3 ? bloomberg.accent.orange : bloomberg.status.negative;
                  })(),
                  fontWeight: 700,
                }}>
                  {new Date(dataStatus.latest_daily_close + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Box>
              </Typography>
            )}
            <Box component="span" sx={{ color: bloomberg.border.primary, fontSize: '0.7rem' }}>|</Box>
            <Typography
              variant="caption"
              sx={{
                color: bloomberg.text.muted,
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.7rem',
              }}
            >
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              {' '}
              <Box component="span" sx={{ color: bloomberg.accent.orange }}>|</Box>
              {' '}
              <LiveClock />
            </Typography>
            {/* Theme toggle */}
            <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton size="small" onClick={toggleMode} sx={{ p: 0.4, color: bloomberg.text.muted, '&:hover': { color: bloomberg.accent.orange } }}>
                {mode === 'dark' ? <LightModeIcon sx={{ fontSize: 15 }} /> : <DarkModeIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
            {/* Hard sync button */}
            <Tooltip title={dataStatus?.historical_sync_running ? 'Syncing data…' : 'Sync historical prices & signals'}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleSync}
                  disabled={dataStatus?.historical_sync_running}
                  sx={{
                    p: 0.4,
                    color: dataStatus?.historical_sync_running ? bloomberg.accent.orange : bloomberg.text.muted,
                    '&:hover': { color: bloomberg.accent.orange },
                  }}
                >
                  {dataStatus?.historical_sync_running
                    ? <CircularProgress size={14} sx={{ color: bloomberg.accent.orange }} />
                    : <SyncIcon sx={{ fontSize: 16 }} />
                  }
                </IconButton>
              </span>
            </Tooltip>
            {isAdmin && (
              <Tooltip title="User administration">
                <IconButton size="small" onClick={() => navigate('/admin')} sx={{ color: bloomberg.text.muted }}>
                  <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, ml: 0.5, display: { xs: 'none', sm: 'block' } }}>
              {username}
            </Typography>
            <Tooltip title="Sign out">
              <IconButton size="small" onClick={() => { logout(); navigate('/'); }} sx={{ color: bloomberg.text.muted }}>
                <LogoutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
        <Sidebar isMobile={isMobile} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <Main>
        <Box sx={{ height: 40 }} />
          <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:sub" element={<Portfolio />} />
            <Route path="/tracker" element={<TrackerPortfolio />} />
            <Route path="/screener" element={<StockScreener />} />
          <Route path="/patterns" element={<Patterns />} />
          <Route path="/market-map" element={<MarketMap />} />
          <Route path="/macro-overview" element={<MacroOverview />} />
          <Route path="/correlation" element={<CorrelationStudies />} />
          <Route path="/charting" element={<Charting />} />
          <Route path="/research" element={<Research />} />
          {/* Ask Research hidden for now */}
          {/* <Route path="/ask-research" element={<ResearchChat />} /> */}
          <Route path="/podcasts" element={<Podcasts />} />
            <Route path="/news" element={<News />} />
            <Route path="/signals" element={<Signals />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/admin" element={<AdminRoute />} />
            {/* Hidden: internal reference docs. Deliberately absent from
              navItems — reachable only by typing the route. */}
          <Route path="/docs" element={<Docs />} />
          <Route path="/stock/:symbol" element={<StockDetail />} />
          </Routes>
      </Main>
      <JobNotifier />
    </Box>
  );
}

// Live clock component for the header
const LiveClock = () => {
  const [time, setTime] = React.useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>{time.toLocaleTimeString('en-US', { hour12: false })}</>
  );
};

export default App;
